<template>
  <div class="font-Relative min-h-full text-app-color flex flex-col bg-grey-450 px-15">
    <LogoModule class="pt-25 md:pl-25 md:absolute" />

    <div class="w-full mx-auto mt-30 md:m-auto max-w-600 pb-60 md:pt-60">
      <div class="m-15">
        <div class="text-center">
          <p class="text-24 md:text-47 font-bold">Welcome back</p>
          <p class="text-20 text-grey-20">Log in using your account</p>
        </div>

        <form class="w-full" @submit.prevent="login">
          <!-- INPUT CONTROLS -->
          <InputModule
            ref="emailInputRef"
            v-model="user.email"
            name="email"
            type="email"
            label="Your email"
            placeholder="email@example.com"
            class="w-full mt-30"
            input-classes="px-22 py-13 rounded-10 border border-grey-10  anim-all text-17"
            label-class="mb-5 font-bold"
            shadow="focus:shadow-signup"
            :has-background="false"
            :error-message="errorMessages.email"
            @clearErrorMessage="clearErrorMessage"
          />

          <InputModule
            v-model="user.password"
            name="password"
            type="password"
            label="Your password"
            placeholder="password"
            class="w-full mt-30"
            input-classes="px-22 py-13 rounded-10 border border-grey-10  anim-all text-17"
            label-class="mb-5 font-bold"
            shadow="focus:shadow-signup"
            :has-background="false"
            :forgot-password="true"
            :error-message="errorMessages.password"
            @clearErrorMessage="clearErrorMessage"
          />

          <ButtonModule
            text="Continue"
            class="w-full justify-center bg-brand hover:bg-brand-hover text-white rounded-10 mt-30 py-16 font-medium text-17"
          />
        </form>

        <SocialAuth v-if="false" text="Login" class="pt-10 md:pt-30" />

        <div class="mt-50 md:mt-100 text-center w-full text-15">
          <p class="text-grey-275">Don’t have an account yet?</p>
          <router-link to="/sign-up" class="text-brand mt-5 hover:underline mb-10 md:mb-35">Sign up</router-link>
        </div>
      </div>
    </div>
    <RestoreAccount ref="restoreModal" :email="user.email" :date="deletedAt" title="Account deleted">
      <p class="text-14 mt-20 mb-25">
        This account was recently deleted and may be recoverable.
        If you want to restore this account, please type a password.
      </p>
    </RestoreAccount>
  </div>
</template>

<script>
import InputModule from '@/components/shared/form-controls/InputModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SocialAuth from '@/components/auth/SocialAuth'
import RestoreAccount from '@/components/auth/RestoreAccount'
import LogoModule from '@/components/shared/LogoModule'

import { requiredFieldsValidation, emailValidation, passwordValidation } from '@/helpers'
import localStorageService from '@/core/local-storage'
import { profileService } from '@/services'

import { defineComponent, ref, reactive, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'LoginView',

  components: { InputModule, ButtonModule, SocialAuth, RestoreAccount, LogoModule },

  setup () {
    const { commit, dispatch } = useStore()
    const router = useRouter()
    const route = useRoute()

    const deletedAt = ref(null)
    const restoreModal = ref(null)

    const emailInputRef = ref(null)

    const user = reactive({
      email: route.query.email || null,
      password: null
    })

    const errorMessages = reactive({
      email: null,
      password: null
    })

    onMounted(() => {
      nextTick(() => {
        commit('setUser', null)
        emailInputRef.value.focus()
      })
    })

    async function loginCount () {
      const user = await profileService.profile()
      const payload = {
        email: user.data.email,
        firstName: user.data.profile.firstName,
        lastName: user.data.profile.lastName,
        showUpgradeToPro: user.data.profile.showUpgradeToPro + 1
      }

      if (user.data.profile.showUpgradeToPro < 10) await profileService.update(payload)
    }

    async function updatePlan () {
      const currentPlan = await dispatch('getCurrentPlan')

      if (currentPlan.planId === +route.query.planId || currentPlan.planId === 9 || currentPlan.planId === 10) {
        router.push({ name: 'Books' })
      } else {
        const curUser = await dispatch('getProfile')

        const data = {
          planId: route.query.planId,
          userId: curUser.id
        }

        dispatch('upgradePlan', data)
          .then(res => {
            commit('loading', true)

            const stripe = window.Stripe(process.env.VUE_APP_STRIPE)
            stripe.redirectToCheckout({ sessionId: res })
              .then(() => {})
              .catch(() => commit('loading', false))
          })
      }
    }

    function login () {
      requiredFieldsValidation(errorMessages, user)

      if (!errorMessages.email) errorMessages.email = emailValidation(user.email)

      if (!errorMessages.password) errorMessages.password = passwordValidation(user.password)

      if (Object.values(errorMessages).every(i => !i)) {
        dispatch('signIn', user).then(() => {
          if (route.query.planId) {
            updatePlan(route.query.planId)
            return
          }

          const savedUrl = route.params.savedUrl

          const p = localStorageService.getItem('lastOpenedProject')
          const accountType = localStorageService.getItem('account-type')

          const url = p ? { name: 'Book', params: { id: p.bookId }, query: { page: p.projectId } } : accountType || null

          loginCount()

          router.push(url || savedUrl || { name: 'Books' })
        }).catch((data) => {
          if (data?.error?.code === 6) {
            deletedAt.value = data.error.message
            restoreModal.value.open()
          } else {
            commit('setNotification', {
              isVisible: true,
              message: 'Incorrect email or password. Please try again.',
              color: 'error'
            })
          }
        })
      }
    }

    function clearErrorMessage (name) {
      errorMessages[name] = null
    }

    return {
      // data
      deletedAt,
      user,
      errorMessages,
      // refs
      restoreModal,
      emailInputRef,
      // functions
      login,
      clearErrorMessage
    }
  }
})
</script>
